import { Injectable } from '@angular/core'
import { RegionalApiClientFactory, RegionalApiClientSetupService } from '@ftr/api-regional'
import { ApiClient } from '@ftr/api-shared'
import { HearingPlaybackData } from '@ftr/contracts/regional-api'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData } from '@ftr/foundation'
import { SttAggregate, SttAggregateSchema } from '@ftr/regional-api-contracts'
import { switchMap } from 'rxjs'
import { RealTimeHearingWithSttRemarks } from '../../types'
import { utterancesToRemarks } from '../../utils'

@Injectable({
  providedIn: 'root',
})
export class RegionalHearingsService extends RegionalApiClientSetupService {
  constructor(regionalApiClientFactory: RegionalApiClientFactory) {
    super(regionalApiClientFactory)
  }

  override regionalApiPath = 'hearing'
  regionalApiHearingSectionPath = 'hearing-section'

  getHearingWithSttRemarks(courtSystemId: Uuid, hearingId: Uuid): ApiResult<RealTimeHearingWithSttRemarks> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(apiClient => {
        return this.getHearingWithStt(apiClient, courtSystemId, hearingId).pipe(
          mapData(({ id, utterances, speakers, lastEdited }) => {
            return {
              id,
              remarks: utterancesToRemarks(utterances, speakers),
              lastEdited: lastEdited ?? undefined,
            }
          }),
        )
      }),
    )
  }

  getHearingSectionWithSttRemarks(
    courtSystemId: Uuid,
    hearingSectionId: Uuid,
  ): ApiResult<RealTimeHearingWithSttRemarks> {
    return this.regionalApiClient(this.regionalApiHearingSectionPath, courtSystemId).pipe(
      switchMap(apiClient => {
        return this.getHearingSectionWithStt(apiClient, courtSystemId, hearingSectionId).pipe(
          mapData(({ id, utterances, speakers, lastEdited }) => {
            return {
              id,
              remarks: utterancesToRemarks(utterances, speakers),
              lastEdited: lastEdited ?? undefined,
            }
          }),
        )
      }),
    )
  }

  getHearingPlayback(courtSystemId: Uuid, hearingId: Uuid): ApiResult<HearingPlaybackData> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(apiClient =>
        apiClient.get<HearingPlaybackData>({
          path: `${hearingId}/playback`,
          headers: { courtSystemId },
          responseBodyType: HearingPlaybackData,
        }),
      ),
    )
  }

  getHearingSectionPlayback(courtSystemId: Uuid, hearingSectionId: Uuid): ApiResult<HearingPlaybackData> {
    return this.regionalApiClient(this.regionalApiHearingSectionPath, courtSystemId).pipe(
      switchMap(apiClient =>
        apiClient.get<HearingPlaybackData>({
          path: `${hearingSectionId}/playback`,
          headers: { courtSystemId },
          responseBodyType: HearingPlaybackData,
        }),
      ),
    )
  }

  private getHearingWithStt(apiClient: ApiClient, courtSystemId: Uuid, hearingId: Uuid): ApiResult<SttAggregate> {
    return apiClient.get({
      path: `${hearingId}/stt`,
      headers: { courtSystemId },
      responseBodyParser: SttAggregateSchema,
    })
  }

  private getHearingSectionWithStt(
    apiClient: ApiClient,
    courtSystemId: Uuid,
    hearingSectionId: Uuid,
  ): ApiResult<SttAggregate> {
    return apiClient.get({
      path: `${hearingSectionId}/stt`,
      headers: { courtSystemId },
      responseBodyParser: SttAggregateSchema,
    })
  }
}
